/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Select 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/select/Select.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 17.
 */
import * as parent from '@components/form/field/Field';
import { Component } from '@base/Component';
import { Loading } from '@elements/loading/Loading';
import { List } from '@components/list/List';
import { Record } from '@models/store/Record';
import { ListStores, TreeStores } from '@models/store/Store';
import { Grouper } from '@models/table/Group';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Select.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<S extends ListStores | TreeStores, M extends boolean, T> extends parent.Properties<T> {
    /**
     * 데이터스토어
     */
    store: S;

    /**
     * popover 사용 여부
     * n=1: $input 박스가 노출/popover 사용
     * n>1: List 바로 노출/popover 사용 안함
     */
    rows?: number;

    /**
     * 다중선택여부
     */
    multiple?: M;

    /**
     * 값이 존재하지 않을때, 자동으로 값을 지정할지 여부
     */
    autoValue?: boolean;

    /**
     * 검색 여부
     */
    search?: boolean;

    /**
     * 선택확인버튼 (multiple 이 true 경우 항상 true)
     */
    confirmButton?: boolean;

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder?: LanguageText;

    /**
     * 그룹설정
     */
    grouper?: Grouper<List<S>>;

    /**
     * 선택된 값을 표시할 store 의 field 명
     */
    displayField?: string;

    /**
     * 폼 전송시 전송될 값을 지정할 store 의 field 명
     */
    valueField?: string;

    /**
     * 목록 항목 너비
     */
    listWidth?: number;

    /**
     * 목록 항목을 보일 때 사용할 렌더링 함수
     */
    listRenderer?: (display: string, record: Record, $column: Dom) => string | Promise<string>;

    /**
     * 목록 항목의 선택가능여부를 반환하는 함수
     */
    listSelectable?: (record: Record) => boolean;

    /**
     * select 폼 INPUT 영역에 사용할 렌더링 함수
     */
    renderer?: (display: string | string[], records: Record | Record[]) => string | Promise<string>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Select<S extends ListStores | TreeStores = ListStores | TreeStores, M extends boolean = boolean>
    extends parent.Field
{
    /**
     * 컴포넌트 확장타입
     */
    variant = 'select';

    /**
     * 데이터스토어
     */
    store: S;

    /**
     * popover 사용 여부
     * n=1: $input 박스가 노출/popover 사용
     * n>1: List 바로 노출/popover 사용 안함
     */
    rows: number = 1;

    /**
     * 다중선택여부
     */
    multiple: M;

    /**
     * 값이 존재하지 않을때, 자동으로 값을 지정할지 여부
     */
    autoValue: boolean;

    /**
     * 검색 여부
     */
    search: boolean = true;

    /**
     * 선택확인버튼 (multiple 이 true 경우 항상 true)
     */
    confirmButton?: boolean;

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder: LanguageText | null;

    /**
     * 선택된 값을 표시할 store 의 field 명
     */
    displayField: string | null = null;

    /**
     * 폼 전송시 전송될 값을 지정할 store 의 field 명
     */
    valueField: string | null = null;

    /**
     * 목록 항목 너비
     */
    listWidth: number = null;

    /**
     * 목록 항목을 보일 때 사용할 렌더링 함수
     */
    listRenderer: (display: string, record: Record, $column: Dom) => string | Promise<string> = null;

    /**
     * 목록 항목의 선택가능여부를 반환하는 함수
     */
    listSelectable: (record: Record) => boolean = null;

    /**
     * select 폼 INPUT 영역에 사용할 렌더링 함수
     */
    renderer: (display: string | string[], records: Record | Record[]) => string | Promise<string> = null;

    /**
     * 리스트
     */
    list: List<S> = null;

    /**
     * 목록을 제외한 입력폼 DOM
     */
    $input: Dom = null;

    /**
     * placeholder DOM
     */
    $placeholder: Dom = null;

    /**
     * 검색 INPUT DOM
     */
    $search: Dom = null;

    /**
     * 디스플레이 DOM
     */
    $display: Dom = null;

    /**
     * 디스플레이 텍스트 DOM
     */
    $text: Dom = null;

    /**
     * 확장 버튼 DOM
     */
    $expand: Dom = null;

    /**
     * 목록이 보일 DOM
     */
    $list: Dom = null;

    /**
     * Record 를 찾지 못한 경우, 임시 저장되는 rawValue
     */
    rawValue: any = null;

    /**
     * 로딩
     */
    loading: Loading = null;

    /**
     * Select 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<S, M, Select> = null) {
        super(properties);

        this.store = this.properties.store;
        this.store.retain();
        this.rows = this.properties?.rows ?? 1;
        this.multiple = (this.properties?.multiple === true) as M;
        this.autoValue = this.properties?.autoValue === true;
        this.search = this.properties?.search === true;
        this.confirmButton = this.multiple == true || this.properties?.confirmButton !== false;
        this.placeholder = this.properties.placeholder ?? null;

        this.displayField = this.properties.displayField ?? 'display';
        this.valueField = this.properties.valueField ?? 'value';
        this.store.setPrimaryKeys([this.valueField], false);

        this.listWidth = this.properties.listWidth ?? null;

        this.listRenderer = this.properties.listRenderer ?? null;
        this.listSelectable = this.properties.listSelectable ?? null;
        this.renderer = this.properties.renderer ?? null;
    }

    /**
     * 목록을 제외한 입력폼을 가져온다.
     *
     * @returns 목록을 제외한 입력폼
     */
    $getInput(): Dom {
        if (this.$input === null) {
            this.$input = Html.create('div', { 'data-role': 'input' });

            if (this.search == true) {
                const $field = Html.create('div', { 'data-role': 'field' });
                $field.append(this.$getDisplay());

                if (this.search == true) {
                    $field.append(this.$getSearch());
                }

                this.$input.append($field);

                if (this.isExpandable() == true) {
                    this.$input.append(this.$getExpand());
                }
            } else {
                this.$input.append(this.$getExpand());
            }
        }

        return this.$input;
    }

    /**
     * 디스플레이 DOM 을 가져온다.
     *
     * @returns 디스플레이 DOM
     */
    $getDisplay(): Dom {
        if (this.$display == null) {
            this.$display = Html.create('div', { 'data-role': 'display' });
            this.$display.append(this.$getText());
        }

        return this.$display;
    }

    /**
     * 디스플레이 텍스트 DOM 을 가져온다.
     *
     * @returns 디스플레이 텍스트 DOM
     */
    $getText(): Dom {
        if (this.$text == null) {
            this.$text = Html.create('div', { 'data-role': 'text' });
        }

        return this.$text;
    }

    /**
     * 검색 INPUT DOM 을 가져온다.
     *
     * @returns 검색 INPUT DOM
     */
    $getSearch(): Dom {
        if (this.$search === null) {
            this.$search = Html.create('input', { type: 'search' });

            if (this.placeholder !== null) {
                Language.resolveText(this.placeholder).then((placeholder) => {
                    this.$search.setAttr('placeholder', placeholder);
                });
            }

            this.$search.on('focus', () => {
                if (this.readonly === true) return;

                this.expand();
            });

            this.$search.on('pointerdown', (_, e) => {
                e.stopImmediatePropagation();
                if (this.readonly === true) return;
            });

            this.$search.on('keydown', (_, e) => {
                if (this.readonly === true) return;

                if (e.key == 'ArrowUp' || e.key == 'ArrowDown' || e.key == 'Space' || e.key == 'Enter') {
                    if (this.isExpanded() == false) {
                        this.expand();
                    } else if (this.isExpanded() == true && this.getList().getStore().isLoaded() == true) {
                        if (e.key == 'ArrowUp' || e.key == 'ArrowDown') {
                            this.getList()
                                .getBody()
                                .moveFocusCell(e.key == 'ArrowUp' ? 'up' : 'down', false);
                        } else {
                            this.getList().onKeyboard(e);
                        }
                    }

                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            });

            this.$search.on('input', async (me, e) => {
                if (this.readonly === true) return;

                if (e.isComposing == true && me.getValue().length == 0) return;

                if (this.isExpanded() == false) {
                    await this.expand();
                }

                const value = me.getValue();
                if (value.length == 0) {
                    await this.getList().getStore().setFilters(null);
                } else {
                    this.getList().getStore().setFilters(null);
                    this.getList()
                        .getStore()
                        .setFilters(
                            {
                                [this.displayField]: { value: value, operator: 'likecode' },
                                [this.valueField]: { value: value, operator: 'likecode' },
                            },
                            'OR'
                        );
                }
            });
        }

        return this.$search;
    }

    /**
     * 확장 버튼 DOM 을 가져온다.
     *
     * @returns 확장 버튼 DOM
     */
    $getExpand(): Dom {
        if (this.$expand === null) {
            this.$expand = Html.create('button', {
                id: this.id + '-focusable',
                type: 'button',
                'data-action': 'expand',
            });

            if (this.search == true) {
                this.$expand.setAttr('tabindex', '-1');
            } else {
                this.$expand.append(this.$getDisplay());
            }

            if (this.isExpandable() == true) {
                const $arrow = Html.create('i');
                this.$expand.append($arrow);
            }

            this.$expand.on('pointerdown', (_, e) => {
                e.stopImmediatePropagation();
                this.toggle();
            });

            this.$expand.on('keydown', (_, e) => {
                if (e.key == 'ArrowUp' || e.key == 'ArrowDown' || e.key == 'Space' || e.key == 'Enter') {
                    if (this.isExpanded() == false) {
                        this.expand();
                    } else if (this.isExpanded() == true && this.getList().getStore().isLoaded() == true) {
                        this.getList().onKeyboard(e);
                    }

                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            });
        }

        return this.$expand;
    }

    /**
     * 목록이 보일 DOM 을 가져온다.
     *
     * @returns 목록이 보일 DOM
     */
    $getList(): Dom {
        if (this.$list === null) {
            this.$list = Html.create('div', { 'data-role': 'list' });

            if (this.rows > 1) {
                this.$list.addClass('attached');
                this.$list.setStyleProperty('--attached-rows', this.rows);
            } else {
                if (this.listWidth !== null) {
                    this.$list.setStyle('min-width', `${this.listWidth}px`);
                }
                this.$list.on('toggle', async (_, e: ToggleEvent) => {
                    if (e.newState === 'closed') {
                        await this.collapse();
                    }
                });
            }
        }

        return this.$list;
    }

    /**
     * 데이터스토어를 가져온다.
     *
     * @returns 데이터스토어
     */
    getStore(): S {
        return this.getList().getStore();
    }

    /**
     * 목록을 가져온다.
     *
     * @returns 리스트
     */
    getList(): List<S> {
        if (this.list === null) {
            this.list = new List<S>({
                store: this.store,
                autoLoad: this.isExpandable() === false,
                layout: this.isExpandable() == true ? 'auto' : 'fit',
                multiple: this.multiple === true,
                autoExpand: true,
                grouper: this.properties?.grouper ?? null,
                selectable: this.listSelectable ?? true,
                dataIndex: (value, record, $column) => {
                    const display = record.get(this.displayField) ?? '';
                    if (this.listRenderer !== null) {
                        return this.listRenderer(display, record, $column);
                    }
                    return display;
                },
                listeners: {
                    beforeLoad: async (me) => {
                        /**
                         * 데이터스토어가 새로 로드되기전 현재 매칭된 값이 존재할 경우, (매칭되면 rawValue 는 NULL 이다.)
                         * 새로 로드된 이후에서 해당값이 존재하는지 확인하기 위해 rawValue 값을 설정한다.
                         */
                        if (this.isInit == true) {
                            if (this.rawValue === null) {
                                const value = await this.getValue();
                                this.rawValue = value;
                            }
                        }
                        this.setLoading(true);
                        me.setMinHeight(100);
                    },
                    load: async (me) => {
                        me.setMinHeight(null);
                        this.setLoading(false);

                        /**
                         * 스토어에서 찾지못한 value가 있거나, 스토어가 새로로드되기전 값이 지정되어 있었다면,
                         * 스토어가 로드된 후 해당 값을 찾기 위해 노력한다.
                         */
                        if (this.isInit == true && this.rawValue !== null) {
                            await this.setValue(this.rawValue, false);
                        }
                    },
                    selectionChange: async (selections) => {
                        await this.#setValue(selections);
                    },
                    rowClick: async (me, is_selection_changed) => {
                        if (
                            this.multiple == false &&
                            (is_selection_changed == true ||
                                me.getTable().getSelection().isSelected(me.getRecord()) == true)
                        ) {
                            await this.collapse();
                        }
                    },
                },
            });
        }

        return this.list;
    }

    /**
     * 디스플레이 영역의 로딩상태여부를 설정한다.
     *
     * @param loading - 로딩여부
     */
    setLoading(loading: boolean): void {
        if (this.loading === null) {
            this.loading = new Loading({
                indicator: 'column',
                direction: 'row',
                text: Language.getAction('LOADING'),
                textAlign: 'left',
            });

            this.loading.setComponent(this);
            this.$getDisplay().append(this.loading.$getElement());
        }

        if (loading == true) {
            this.loading.show();
        } else {
            this.loading.hide();
        }
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: any, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        value = await this.#matchValue(value);

        await this.#setValue(value, is_init, false);

        if (this.isExpandable() == false) {
            await this.getList()
                .getSelection()
                .restoreSelections(await this.#getValue(), false);
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<M extends true ? any[] : any> {
        const rawValue = await this.#getValue();

        if (rawValue == null) {
            return null;
        } else {
            if (this.multiple == true) {
                const values = [];
                rawValue.forEach((value) => {
                    values.push(value.get(this.valueField));
                });
                return values;
            } else {
                return rawValue[0].get(this.valueField);
            }
        }
    }

    /**
     * 필드값에 해당하는 레코드를 가져온다.
     *
     * @returns 필드값에 해당하는 레코드
     */
    async getValueToRecord(): Promise<M extends true ? Record[] : Record> {
        const rawValue = await this.#getValue();

        if (rawValue == null) {
            return null;
        } else {
            if (this.multiple == true) {
                return rawValue as M extends true ? Record[] : Record;
            } else {
                return rawValue[0] as M extends true ? Record[] : Record;
            }
        }
    }

    /**
     * 필드값에 해당하는 첫번째 레코드를 가져온다.
     *
     * @returns 필드값에 해당하는 첫번째 레코드
     */
    async getValueToRecordOne(): Promise<Record | null> {
        const records = await this.getValueToRecord();
        if (Array.isArray(records) == true) {
            return records[0] ?? null;
        }

        return records;
    }

    /**
     * 특정값을 가진 레코드를 검색한다.
     *
     * @param value - 검색할 값
     * @returns 검색된 레코드
     */
    async findRecord(value: any): Promise<Record | null> {
        if (this.getStore().isLoaded() == false) {
            await this.getStore().load();
        }

        return await this.getStore().find({ [this.valueField]: value });
    }

    /**
     * 목록이 확장가능한지 확인한다.
     *
     * @returns 목록확장가능여부
     */
    isExpandable(): boolean {
        return this.rows === 1;
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     */
    focus(): void {
        if (this.search === true) {
            this.$getSearch().focus();
        } else {
            this.$getExpand().focus();
        }
    }

    /**
     * 필드값을 초기화한다.
     *
     * @param is_fire_event - 이벤트발생여부
     * @param value - 초기화할 값 (undefined 인 경우 초기값)
     */
    async reset(is_fire_event: boolean = true, value: any): Promise<void> {
        super.reset(is_fire_event, value);

        if (value === null) {
            if (this.search == true && this.$search !== null) {
                this.$search.setValue('');
            }

            await this.getList().getStore().setFilters(null);
        }

        await this.#updateDisplay();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_lock - 큐 예약이 필요한지 여부
     */
    async #setValue(value: Record | Record[], is_init: boolean = false, is_lock: boolean = true): Promise<void> {
        if (is_lock == true) {
            await this.lockValue();
        }

        if (value == null || (Array.isArray(value) == true && value.length == 0)) {
            this.value = null;
        } else {
            if (this.multiple == true) {
                if (Array.isArray(value) == true) {
                    this.value = value;
                } else {
                    this.value = [value];
                }
            } else {
                if (Array.isArray(value) == true) {
                    this.value = value[0];
                } else {
                    this.value = value;
                }
            }
        }

        await this.#updateDisplay();

        if (is_lock == true) {
            await this.releaseValue(value, is_init);
        }
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async #getValue(): Promise<Record[] | null> {
        if (this.value === null || (Array.isArray(this.value) == true && this.value.length == 0)) {
            return null;
        }

        return Array.isArray(this.value) == true ? this.value : [this.value];
    }

    /**
     * 목록의 위치를 상단 또는 하단으로 변경하고, 목록의 최대 높이를 지정한다.
     *
     * @param contentRect - content 영역의 DOMRect (미전달시 함수 내에서 계산)
     */
    #updatePosition(contentRect?: DOMRect): void {
        this.getList().setMaxHeight(null);

        contentRect ??= this.$getContent().getEl().getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceAbove = contentRect.top;
        const spaceBelow = viewportHeight - contentRect.bottom;

        const listHeight = this.$getList().getEl().getBoundingClientRect().height;
        const isBottom = spaceBelow >= spaceAbove || spaceBelow > listHeight;

        this.$getList().setStyle('position-area', isBottom ? 'bottom span-x-end' : 'top span-x-end');
        this.getList().setMaxHeight((isBottom ? spaceBelow : spaceAbove) - 50);
    }

    /**
     * 목록을 확장한다.
     */
    async expand(): Promise<void> {
        if (this.isExpanded() == true) {
            return;
        }

        if (this.isDisabled() === true) {
            return;
        }

        if (this.readonly === true) {
            return;
        }

        this.#preventScroll(true);

        const contentRect = this.$getContent().getEl().getBoundingClientRect();

        this.#updatePosition(contentRect);
        this.$getList().getEl().showPopover();

        if (this.getList().getStore().isLoaded() == false) {
            await this.getList().getStore().load();
        }

        this.#updatePosition(contentRect);

        await this.getList().getBody().blurCell();
        await this.getList()
            .getSelection()
            .restoreSelections(await this.#getValue(), true, false);
    }

    /**
     * 목록을 숨긴다.
     */
    async collapse(): Promise<void> {
        if (this.isExpanded() == true && this.isExpandable() == true) {
            this.$getList().getEl().hidePopover();

            if (this.search === true) {
                this.$getExpand().focus();
            } else {
                this.$getExpand().focus();
            }
        }

        this.#preventScroll(false);
    }

    /**
     * 목록을 토글한다.
     */
    async toggle(): Promise<void> {
        if (this.isExpandable() == false) {
            return;
        }

        if (this.isDisabled() === true) {
            return;
        }

        if (this.isExpanded() == true) {
            await this.collapse();
        } else {
            await this.expand();
        }
    }

    /**
     * 목록이 보이는 상태인지 확인한다.
     *
     * @returns 리스트가 보이는지 여부
     */
    isExpanded(): boolean {
        if (this.rows > 1) {
            return true;
        }

        return this.$getList().getEl().matches(':popover-open');
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        if (readonly == true) {
            this.$getInput().setAttr('readonly', 'readonly');

            if (this.search === true) {
                this.$getSearch().setAttr('readonly', 'readonly');
            }
        } else {
            this.$getInput().removeAttr('readonly');

            if (this.search === true) {
                this.$getSearch().removeAttr('readonly');
            }
        }

        super.setReadonly(readonly);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$getInput().setDisabled(disabled);
        this.$getExpand().setDisabled(disabled);
        if (this.search === true) {
            this.$getSearch().setDisabled(disabled);
        }

        this.getList().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.$getInput());
        this.$getList().append(this.getList().$getComponent());
        this.$getContent().append(this.$getList());

        if (this.isExpandable() == true) {
            this.$getList().setAttr('popover', 'manual');
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.getList().isRendered() == false) {
            await this.getList().render();
        }

        await super.renderState();
        await this.#updateDisplay();
    }

    /**
     * 컴포넌트 포커스 해제시 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onBlur(): Promise<boolean> {
        await this.collapse();

        return await super.onBlur();
    }

    /**
     * 필드 상위 컴포넌트 중 스크롤이 되는 컴포넌트를 찾아 스크롤을 제어한다.
     *
     * @param is_prevent - 스크롤 차단여부
     */
    private scrollableParent: Component;
    #preventScroll(is_prevent: boolean): void {
        if (this.scrollableParent === undefined) {
            this.scrollableParent = null;
            let parent = this.getParent();
            while (parent !== null) {
                if (parent.getScroller()?.isScrollable('y') === true) {
                    this.scrollableParent = parent;
                    break;
                }

                parent = parent.getParent();
            }
        }

        this.scrollableParent?.getScroller()?.preventScroll('y', is_prevent);
    }

    /**
     * 디스플레이 영역을 업데이트한다.
     */
    async #updateDisplay(): Promise<void> {
        const rawValue = await this.#getValue();

        if (rawValue === null) {
            this.$getText().addClass('empty');
            this.$getText().html(this.placeholder ?? '');
        } else {
            let display: string;
            if (this.renderer !== null) {
                if (this.multiple == true) {
                    const displayTexts: string[] = [];
                    rawValue.forEach((record) => {
                        displayTexts.push(record.get(this.displayField));
                    });

                    display = await this.renderer(displayTexts, rawValue);
                } else {
                    display = await this.renderer(rawValue[0].get(this.displayField), rawValue[0]);
                }
            } else {
                if (this.multiple == true && rawValue.length > 1) {
                    display = await Language.get('texts.selections', {
                        value: rawValue[0].get(this.displayField),
                        count: (rawValue.length - 1).toString(),
                    }).getText();
                } else {
                    display = rawValue[0].get(this.displayField);
                }
            }

            this.$getText().removeClass('empty');
            this.$getText().html(display ?? '');
        }
    }

    /**
     * 특정값으로 스토어에서 레코드를 가져온다.
     *
     * @param value - 레코드를 검색할 값
     * @returns 검색된 레코드
     */
    async #matchValue(value: any): Promise<Record | Record[]> {
        if (value !== null) {
            if (this.store.isLoaded() == false) {
                await this.store.load();
            }

            this.rawValue = value;

            const values = Array.isArray(value) == true ? value : [value];
            let is_matched: boolean = true;
            const matches: Record[] = [];
            for (const value of values) {
                const match = await this.findRecord(value);

                if (match === null) {
                    is_matched = false;
                } else {
                    matches.push(match);
                }
            }

            if (is_matched == true) {
                value = matches;
                this.rawValue = null;
            } else {
                value = null;
            }
        }

        if (value === null && this.autoValue == true && this.getStore().getCount() > 0) {
            let firstValue = null;
            const store = this.getStore();
            if (store.type == 'tree') {
                firstValue = await store.getAt([0]);
            } else {
                firstValue = await store.getAt(0);
            }

            if (firstValue !== null) {
                if (this.multiple == true) {
                    value = [firstValue];
                } else {
                    value = firstValue;
                }
            }
        }

        return value;
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.collapse();
        this.list?.remove(false);
        this.list = null;

        this.store.release();

        await super.remove(removeDom);
    }
}
